import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './ai-ad-creative-generator.css';
import aiAdCreativeImg from '../../../assets/images/ai ad creative.png';
import qrCodeImg from '../../../assets/images/ai-ad-creative.png';

const AIAdCreativeGenerator = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        AI <span>Ad Creative Generator</span> – Full Stack SaaS Platform
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Last Updated On: 8th March 2026
      </motion.p>

      <div className="project-content">

        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >

          <img src={aiAdCreativeImg} alt="AI Ad Creative Generator" className="project-image" />

          <h2 className="project-subtitle">AI Ad Creative Generator</h2>

          <span className="project-category">
            Full Stack SaaS Web Application
          </span>

          <p className="project-author">
            Author: Praveen Kumar B
          </p>

          <ProjectStatsBadge docId="aiAdCreativeGenerator" trackView={true} />

          <div className="project-buttons">

            <a
              href="https://ai-ad-creative-frontend.vercel.app/"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → Visit the Project
            </a>

            <a
              href="https://github.com/Praveenkumar-king"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → View on GitHub
            </a>

            <ShareProjectButton
              project={{
                id: "aiAdCreativeGenerator",
                title: "AI Ad Creative Generator",
                path: "/ai-ad-creative-generator"
              }}
            />

          </div>
              
               <div className="qr-container">
            <img src={qrCodeImg} alt="QR Code" className="qr-image" />
          </div>

        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >

          <h3 className="section-title">Project Details</h3>

          <p className="overview-text">
            Hi Friends 👋, Here’s a complete overview of the 
            <strong> AI Ad Creative Generator</strong>.
          </p>


          <h3 className="section-title">Overview ✨</h3>

          <p className="overview-description">
            AI AD Creative is a modern, AI-powered advertising platform designed to simplify and accelerate digital campaign creation. The platform enables users to generate complete advertising campaigns from a single campaign brief, including AI-generated visuals, optimized marketing copy, hashtags, brand voice, and ready-to-publish creative assets. 🤖🎨
          </p>


          <h3 className="section-title">Key Features 🛠️</h3>

          <ul className="feature-list">

            <li>
              AI Campaign Generation 🤖 – Generate complete advertising campaigns using a single product or campaign brief.
            </li>

            <li>
              AI Image Generation 🎨 – Create customized advertising visuals based on user-defined campaign requirements.
            </li>

            <li>
              AI Copy Generation ✍️ – Generate engaging captions, marketing copy, and relevant hashtags for social media campaigns.
            </li>

            <li>
              Multi-Platform Advertising 📱 – Create campaign content optimized for platforms such as Instagram, Facebook, LinkedIn, and X.
            </li>

            <li>
              Brand Voice Tuning 🎯 – Generate content in selectable tones such as Professional, Witty, Urgent, and Inspirational.
            </li>

            <li>
              Creative Studio 🖼️ – Review, customize, edit, and prepare generated creative assets before publishing.
            </li>

            <li>
              Campaign History 📚 – Maintain a history of previously generated campaigns with detailed campaign information.
            </li>

            <li>
              Credit-Based Generation 💳 – Users receive monthly credits for campaign generation with controlled usage limits.
            </li>

            <li>
              Subscription System 💎 – Subscription plans provide additional access and increased campaign generation capabilities.
            </li>

            <li>
              Secure User Authentication 🔐 – Protected user registration, email verification, login, password reset, and JWT-based authentication.
            </li>

            <li>
              User Dashboard 📊 – Centralized dashboard for campaign activity, credits, account information, and platform updates.
            </li>

            <li>
              Support Ticket System 🎫 – Users can create and track support requests with dedicated ticket IDs and status updates.
            </li>

            <li>
              Security Center 🛡️ – Centralized security area for account activity, password management, and security-related information.
            </li>

            <li>
              Campaign Tracking 🔎 – Track generated campaigns and access complete campaign details through dedicated views.
            </li>

            <li>
              Product Updates 📢 – Keep users informed about new features, improvements, and platform updates.
            </li>

            <li>
              Responsive UI 📱💻 – Premium responsive interface optimized for desktop, tablet, and mobile devices.
            </li>

          </ul>


          <h3 className="section-title">Advanced Features 🚀</h3>

          <ul className="feature-list">

            <li>
              AI-Powered Marketing Workflow ⚡ – Combines image generation and AI text generation into a streamlined campaign creation workflow.
            </li>

            <li>
              Brand-Focused Creative Generation 🎯 – Generate marketing content based on campaign objectives, brand voice, CTA, and additional creative requirements.
            </li>

            <li>
              Secure Cloud Asset Management ☁️ – Supports secure storage and delivery of uploaded creative assets using Cloudinary.
            </li>

            <li>
              Transactional Email System 📧 – Automated emails for registration, verification, welcome messages, password-related actions, support tickets, and other platform operations.
            </li>

            <li>
              Live Currency Conversion 💱 – Subscription pricing can be displayed using supported currencies with live exchange-rate data.
            </li>

            <li>
              Terms & Privacy Compliance ⚖️ – Mandatory Terms & Conditions acceptance and dedicated legal/privacy information.
            </li>

            <li>
              Admin Management Panel ⚙️ – Administrative management of users, campaigns, support tickets, feedback, product updates, announcements, and platform operations.
            </li>

          </ul>


          <h3 className="section-title">Why AI AD Creative? 💡</h3>

          <p className="overview-description">
            AI AD Creative brings multiple advertising tasks into a single intelligent workflow. Instead of manually creating visuals, writing copy, selecting hashtags, and preparing platform-specific content separately, users can provide their campaign requirements and generate a complete advertising creative through an integrated AI-powered platform.
          </p>

          <p className="overview-description">
            It helps reduce repetitive work, accelerate creative experimentation, maintain consistent brand communication, and make digital advertising production more efficient.
          </p>


          <h3 className="section-title">Conclusion 🎯</h3>

          <p className="overview-description">
            AI AD Creative is built to simplify the modern advertising workflow by combining AI-powered creativity, intelligent copywriting, brand personalization, campaign management, and secure cloud-based services into one platform.
          </p>

          <p className="overview-description">
            The platform is designed to help businesses and marketers move from idea → creative → campaign faster while maintaining flexibility, consistency, and control.
          </p>

        </motion.div>

      </div>
    </motion.div>
  );
};

export default AIAdCreativeGenerator;